import React from "react";
import {useTranslation} from "react-i18next";
import {useRetryCountdown} from "../utils/retryAfter";

/**
 * 区画（アクティビティ／ホテル／フライト／在庫）ごとのエラー表示と再読み込みボタン。
 * issue #2992 / 報告書 §8 No.4
 *
 * ■ なぜ区画別にするか（2026-08-31 本番負荷テスト）
 *   混雑で一部の取得が失敗すると、従来は全画面エラー（`/plan`）か「検索結果なし」の
 *   誤表示（ホテル）か、無言の欠落（フライト）になっていた。
 *   利用者はページごと再読み込みするため、1人あたり約20回
 *   （OTS 約10 ＝ plan 3 ＋ stocks 3〜4 ＋ hotel 2〜3 ＋ 総数1、フライト約10）の
 *   上流コールが再発生していた。区画別なら 3〜10 回に減る。
 *
 * ■ なぜカウントダウンするか
 *   失敗した利用者は同じタイミングで一斉に再読み込みする。サーバー指示の秒数
 *   （サーキットブレーカの遮断残り時間・既定 20 秒）に 10〜40 秒のランダムを足して散らす。
 *   連打しても `start` を呼び直すまで秒数は再計算されない＝待ち時間を短縮できない。
 */

type Props = {
  /** サーバーが返した retryAfterSec（0 や未指定でもランダム分は待たせる） */
  retryAfterSec?: number;
  /** 再読み込みの実処理。★失敗した区画だけを再要求すること（ページ全体を読み直さない） */
  onRetry: () => void;
  /** 再読み込み中は非活性にする */
  isLoading?: boolean;
  /** 見出しの差し替え（省略時は「取得できませんでした」） */
  message?: string;
  /**
   * ボタン文言の差し替え（省略時は「再読み込み」）。
   * ★料金確認ページのように「何を取り直すのか」を明示したい場合に渡す（2026-09-07）。
   */
  buttonLabel?: string;
};

const SectionRetry: React.FC<Props> = ({retryAfterSec, onRetry, isLoading, message, buttonLabel}) => {
  const {t} = useTranslation();
  const {remainSec, canRetry, start} = useRetryCountdown();

  // 区画エラーが出た時点でカウントダウンを開始する。
  // retryAfterSec が変わった（＝新しい失敗）ときだけ引き直す
  React.useEffect(() => {
    start(retryAfterSec);
  }, [retryAfterSec, start]);

  const handleClick = () => {
    if (!canRetry || isLoading) {
      return;
    }
    onRetry();
    // 押したあとも次の失敗に備えて待ち時間を引き直す（連打防止）
    start(retryAfterSec);
  };

  return (
    <div className="pt-30 pb-30 ta-c">
      <p className="fz-14 fz-16-md fw-b mb-10">
        {message ?? t("情報を取得できませんでした。")}
      </p>
      <p className="fz-12 fz-14-md mb-20">
        {t("ただいま大変混雑しています。しばらくお待ちいただき、再読み込みしてください。")}
      </p>
      <button
        type="button"
        className={canRetry && !isLoading ? "button-medium-orange padding-large" : "button-medium-disabled"}
        disabled={!canRetry || isLoading}
        onClick={handleClick}
      >
        {canRetry
          ? (buttonLabel ?? t("再読み込み"))
          : `${buttonLabel ?? t("再読み込み")}（${remainSec}${t("秒後")}）`}
      </button>
    </div>
  );
};

export default SectionRetry;
